import {
  $toggleLink,
  LinkAttributes,
  LinkNode,
  TOGGLE_LINK_COMMAND
} from '@lexical/link';
import { mergeRegister } from '@lexical/utils';
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  createCommand,
  LexicalCommand,
  LexicalEditor,
  PASTE_COMMAND,
  RangeSelection
} from 'lexical';
import { PasteCommandType } from 'lexical/LexicalCommands';
import {
  extractMeaningfulTextFromUrl,
  extractValidUrl
} from '../utils/link.utils';
import { containsElementNodes } from '../utils/editor-node.utils';

/**
 * Inserts a link at the current selection the same way a pasted URL would:
 * a collapsed selection gets a new text node (resolved via the editor's
 * link-text rules), a range selection is wrapped as-is.
 */
export const INSERT_LINK_COMMAND: LexicalCommand<string> = createCommand();

/**
 * Link payload with URL and optional attributes
 */
type LinkPayload = {
  url: string;
  target?: string;
  rel?: string;
  title?: string;
};

/**
 * Registers link functionality in the Lexical editor
 *
 * @param editor - The Lexical editor instance
 * @param validateUrl - Optional function to validate URLs
 * @param attributes - Optional default attributes for links
 * @returns Function to unregister the link handlers
 */
export function registerLink(
  editor: LexicalEditor,
  validateUrl?: (url: string) => boolean,
  attributes?: LinkAttributes,
  resolveLinkText: (url: string) => string = extractMeaningfulTextFromUrl
) {
  if (!editor.hasNodes([LinkNode])) {
    throw new Error('LinkPlugin: LinkNode not registered on editor');
  }

  return mergeRegister(
    registerToggleLinkCommand(editor, validateUrl, attributes),
    registerPasteUrlHandler(editor, validateUrl, attributes, resolveLinkText),
    registerInsertLinkCommand(editor, attributes, resolveLinkText)
  );
}

/**
 * Registers the INSERT_LINK_COMMAND handler
 *
 * @param editor - The Lexical editor instance
 * @param attributes - Optional default attributes for links
 * @param resolveLinkText - Resolves the text for links inserted without a selection
 * @returns Function to unregister the command
 */
function registerInsertLinkCommand(
  editor: LexicalEditor,
  attributes?: LinkAttributes,
  resolveLinkText: (url: string) => string = extractMeaningfulTextFromUrl
) {
  return editor.registerCommand(
    INSERT_LINK_COMMAND,
    url => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        return false;
      }

      return handleUrlPaste(
        selection,
        url,
        editor,
        resolveLinkText,
        attributes
      );
    },
    COMMAND_PRIORITY_HIGH
  );
}

/**
 * Registers the TOGGLE_LINK_COMMAND handler
 *
 * @param editor - The Lexical editor instance
 * @param validateUrl - Optional function to validate URLs
 * @param attributes - Optional default attributes for links
 * @returns Function to unregister the command
 */
function registerToggleLinkCommand(
  editor: LexicalEditor,
  validateUrl?: (url: string) => boolean,
  attributes?: LinkAttributes
) {
  return editor.registerCommand(
    TOGGLE_LINK_COMMAND,
    payload => {
      if (payload === null) {
        $toggleLink(null);
        return true;
      }

      if (typeof payload === 'string') {
        const url = payload;
        const isValid = validateUrl === undefined || validateUrl(url);

        if (isValid) {
          $toggleLink(url, attributes);
          return true;
        }

        return false;
      }

      const { url, target, rel, title } = payload as LinkPayload;
      $toggleLink(url, {
        ...attributes,
        rel,
        target,
        title
      });

      return true;
    },
    COMMAND_PRIORITY_LOW
  );
}

/**
 * Applies a link to existing selected text
 *
 * @param selection - The current text selection
 * @param url - The URL to link to
 * @param editor - The Lexical editor instance
 * @param attributes - Optional attributes for the link
 * @returns True if successful
 */
function applyLinkToSelection(
  selection: RangeSelection,
  url: string,
  editor: LexicalEditor,
  attributes?: LinkAttributes
): boolean {
  if (containsElementNodes(selection)) {
    return false;
  }

  editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
    ...attributes,
    url
  });

  return true;
}

/**
 * Creates a text node with URL text and applies a link to it
 *
 * @param selection - The current selection point
 * @param url - The URL to create a link for
 * @param editor - The Lexical editor instance
 * @param attributes - Optional attributes for the link
 * @returns True if successful
 */
function createTextNodeWithLink(
  selection: RangeSelection,
  url: string,
  editor: LexicalEditor,
  resolveLinkText: (url: string) => string,
  attributes?: LinkAttributes
): boolean {
  const linkText = resolveLinkText(url);
  const textNode = $createTextNode(linkText);
  selection.insertNodes([textNode]);
  textNode.select();

  editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
    ...attributes,
    url
  });

  return true;
}

/**
 * Handles URL pasting based on the current selection state
 *
 * @param selection - The current editor selection
 * @param url - The URL to process
 * @param editor - The Lexical editor instance
 * @param attributes - Optional attributes for links
 * @returns True if the paste was handled
 */
function handleUrlPaste(
  selection: RangeSelection,
  url: string,
  editor: LexicalEditor,
  resolveLinkText: (url: string) => string,
  attributes?: LinkAttributes
): boolean {
  if (!selection.isCollapsed()) {
    return applyLinkToSelection(selection, url, editor, attributes);
  } else {
    return createTextNodeWithLink(
      selection,
      url,
      editor,
      resolveLinkText,
      attributes
    );
  }
}

/**
 * Registers the PASTE_COMMAND handler for URL pasting
 *
 * @param editor - The Lexical editor instance
 * @param validateUrl - Function to validate URLs
 * @param attributes - Optional default attributes for links
 * @returns Function to unregister the command
 */
function registerPasteUrlHandler(
  editor: LexicalEditor,
  validateUrl?: (url: string) => boolean,
  attributes?: LinkAttributes,
  resolveLinkText: (url: string) => string = extractMeaningfulTextFromUrl
) {
  // Skip handler registration if no URL validation is provided
  if (validateUrl === undefined) {
    return () => {
      // No-op unregister function
    };
  }

  return editor.registerCommand(
    PASTE_COMMAND,
    (event: PasteCommandType) => {
      const clipboardEvent = event as ClipboardEvent;
      const url = extractValidUrl(clipboardEvent, validateUrl);

      if (url === null) {
        return false;
      }

      const selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        return false;
      }

      const handled = handleUrlPaste(
        selection,
        url,
        editor,
        resolveLinkText,
        attributes
      );

      if (handled) {
        // prevent default lexical paste handling
        event.preventDefault();
      }

      return handled;
    },
    COMMAND_PRIORITY_HIGH
  );
}
